import React, { useState, useEffect, useRef } from "react";
import { Play, Pause, Square, Coffee, History, Loader2 } from "lucide-react";
import { Button } from "../ui/Button";
import { useNavigate } from "react-router-dom";
import { processAttendance, AttendanceStatus } from "../../services/attendanceApi";
import { useToast } from "../../hooks/useToast";

type TimerStatus = "Running" | "Paused" | "On Break" | "Stopped";

const TimeTrackerCard = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [status, setStatus] = useState<TimerStatus>(() =>
    (localStorage.getItem("attendance_status") as TimerStatus) || "Stopped"
  );
  const [accumulatedSeconds, setAccumulatedSeconds] = useState(() => {
    const val = localStorage.getItem("attendance_accumulated");
    return val ? parseInt(val, 10) : 0;
  });
  const [currentSessionStart, setCurrentSessionStart] = useState<number | null>(() => {
    const val = localStorage.getItem("attendance_session_start");
    return val ? parseInt(val, 10) : null;
  });
  const [startTime, setStartTime] = useState<string | null>(() =>
    localStorage.getItem("attendance_start_time")
  );
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Sync to localStorage
  useEffect(() => {
    localStorage.setItem("attendance_status", status);
    localStorage.setItem("attendance_accumulated", accumulatedSeconds.toString());
    if (currentSessionStart) {
      localStorage.setItem("attendance_session_start", currentSessionStart.toString());
    } else {
      localStorage.removeItem("attendance_session_start");
    }
    if (startTime) {
      localStorage.setItem("attendance_start_time", startTime);
    } else {
      localStorage.removeItem("attendance_start_time");
    }
  }, [status, accumulatedSeconds, currentSessionStart, startTime]);

  // Update display timer
  useEffect(() => {
    const updateDisplay = () => {
      if (status === "Running" && currentSessionStart) {
        const elapsed = Math.floor((Date.now() - currentSessionStart) / 1000);
        setDisplaySeconds(accumulatedSeconds + elapsed);
      } else {
        setDisplaySeconds(accumulatedSeconds);
      }
    };

    updateDisplay();
    if (status === "Running") {
      timerRef.current = setInterval(updateDisplay, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [status, accumulatedSeconds, currentSessionStart]);

  const formatTime = (totalSeconds: number) => {
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return [hrs, mins, secs]
      .map((v) => (v < 10 ? "0" + v : v))
      .join(":");
  };

  const handleStart = async () => {
    setLoading(true);
    try {
      const apiStatus: AttendanceStatus = status === "Stopped" ? "clock_in" : "resume_work";
      await processAttendance(apiStatus);

      const now = Date.now();
      if (status === "Stopped") {
        const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        setStartTime(time);
        setAccumulatedSeconds(0);
      }

      setCurrentSessionStart(now);
      setStatus("Running");
      toast.success(status === "Stopped" ? "Clocked in successfully" : "Work resumed");
    } catch (error: any) {
      toast.error(error?.message || "Failed to update attendance");
    } finally {
      setLoading(false);
    }
  };

  const handleBreak = async () => {
    setLoading(true);
    try {
      await processAttendance("break");

      const now = Date.now();
      if (currentSessionStart) {
        const elapsed = Math.floor((now - currentSessionStart) / 1000);
        setAccumulatedSeconds(prev => prev + elapsed);
      }
      setCurrentSessionStart(null);
      setStatus("On Break");
      toast.success("Break started");
    } catch (error: any) {
      toast.error(error?.message || "Failed to start break");
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    setLoading(true);
    try {
      await processAttendance("clock_out");
      setStatus("Stopped");
      setStartTime(null);
      setAccumulatedSeconds(0);
      setCurrentSessionStart(null);
      localStorage.removeItem("attendance_status");
      localStorage.removeItem("attendance_accumulated");
      localStorage.removeItem("attendance_session_start");
      localStorage.removeItem("attendance_start_time");
      toast.success("Clocked out successfully");
    } catch (error: any) {
      toast.error(error?.message || "Failed to clock out");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "Running": return "text-green-600 bg-green-50";
      case "Paused": return "text-yellow-600 bg-yellow-50";
      case "On Break": return "text-blue-600 bg-blue-50";
      case "Stopped": return "text-gray-600 bg-gray-50";
      default: return "";
    }
  };

  return (
    <div className="bg-white rounded-xl p-5 flex flex-col h-full border border-gray-100 transition-all duration-300">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-lg font-bold text-[#402B69]">Time Tracker</h2>
        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusColor()}`}>
          {status}
        </span>
      </div>

      <div className="flex flex-col items-center justify-center flex-1 py-4">
        <div className="text-3xl font-mono font-medium text-[#1E1E1E] mb-1">
          {formatTime(displaySeconds)}
        </div>
        <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-6">CURRENT SESSION</p>

        <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
          <Button
            className="bg-[#885ABB] hover:bg-[#724a9d] text-white h-10 gap-2 font-bold text-sm"
            onClick={handleStart}
            disabled={loading || status === "Running"}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} fill="currentColor" />}
            {status === "Stopped" ? "Start" : status === "Running" ? "Active" : "Resume"}
          </Button>

          <Button
            variant="outline"
            className="border-blue-100 text-blue-700 hover:bg-blue-50 h-10 gap-2 font-bold text-sm"
            disabled={loading || status === "Stopped" || status === "On Break"}
            onClick={handleBreak}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Coffee size={16} />} Break
          </Button>

          <Button
            variant="outline"
            className="border-red-50 text-red-600 hover:bg-red-50 h-10 gap-2 font-bold text-sm"
            disabled={loading || status === "Stopped"}
            onClick={handleStop}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Square size={16} fill="currentColor" />} Stop
          </Button>

          <Button
            variant="outline"
            className="border-gray-100 text-gray-700 hover:bg-gray-50 h-10 gap-2 font-bold text-sm"
            onClick={() => navigate("/manage/employee/timesheets")}
            disabled={loading}
          >
            <History size={16} /> Timesheets
          </Button>
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-gray-100 grid grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest mb-0.5">Start Time</p>
          <p className="text-xs font-bold text-[#1E1E1E]">{startTime || "--:--"}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest mb-0.5">Total Active</p>
          <p className="text-xs font-semibold text-[#1E1E1E]">{formatTime(displaySeconds)}</p>
        </div>
      </div>
    </div>
  );
};

export default TimeTrackerCard;
